import React, { useState, useEffect } from 'react';

const AISearchProgress = ({ isSearching, searchMode, onCancel, searchQuery, hasJobDescription, hasRequiredSkills }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  // Generate contextual tips based on what the user provided
  const getSearchTips = () => {
    const tips = [];

    // Base tips - always show
    tips.push({
      icon: "💡",
      text: "AI-Native search uses advanced LLM agents to deeply understand your requirements"
    });

    // Query-only tips (no JD, no skills)
    if (!hasJobDescription && !hasRequiredSkills) {
      tips.push({
        icon: "📝",
        text: "Tip: Add a Job Description to significantly improve match accuracy"
      });
      tips.push({
        icon: "🎯",
        text: "Tip: Specify Required Skills for precise candidate filtering"
      });
      tips.push({
        icon: "⚡",
        text: "Pro tip: JD + Skills combination delivers the best matching results"
      });
    }
    // Has JD but no skills
    else if (hasJobDescription && !hasRequiredSkills) {
      tips.push({
        icon: "✓",
        text: "Great! Job Description provided - AI will extract key requirements"
      });
      tips.push({
        icon: "🎯",
        text: "Tip: Add specific Required Skills for even more precise filtering"
      });
      tips.push({
        icon: "📊",
        text: "AI agents are analyzing role responsibilities and qualifications"
      });
    }
    // Has skills but no JD
    else if (!hasJobDescription && hasRequiredSkills) {
      tips.push({
        icon: "✓",
        text: "Great! Required Skills specified for targeted candidate matching"
      });
      tips.push({
        icon: "📝",
        text: "Tip: Add a Job Description for contextual understanding of the role"
      });
      tips.push({
        icon: "📊",
        text: "AI will match candidates based on your specified skill requirements"
      });
    }
    // Has both JD and skills - optimal search
    else {
      tips.push({
        icon: "🌟",
        text: "Optimal setup! JD + Skills enables the most accurate AI matching"
      });
      tips.push({
        icon: "🤖",
        text: "AI agents are performing deep analysis with full context"
      });
      tips.push({
        icon: "📊",
        text: "Expect highly relevant results with comprehensive skill matching"
      });
    }

    // General tips about AI search
    tips.push({
      icon: "⏱️",
      text: "AI-Native search takes ~60-90 seconds for thorough analysis"
    });
    tips.push({
      icon: "🧠",
      text: "Multiple AI agents collaborate to evaluate each candidate"
    });

    return tips;
  };

  const searchTips = getSearchTips();

  const aiSearchSteps = [
    {
      name: "Search Strategy Agent",
      description: "AI analyzing search requirements and strategy",
      icon: "🧠",
      duration: 2000
    },
    {
      name: "Database Search Agent", 
      description: "Finding and retrieving candidate profiles",
      icon: "🔍",
      duration: 1500
    },
    {
      name: "Profile Matching Agent",
      description: "AI evaluating candidate fit with deep analysis",
      icon: "🎯", 
      duration: 3000
    },
    {
      name: "Ranking Specialist Agent",
      description: "AI applying advanced ranking algorithms",
      icon: "📊",
      duration: 2000
    },
    {
      name: "Results Generation",
      description: "Finalizing AI insights and recommendations",
      icon: "✨",
      duration: 1000
    }
  ];

  const fastSearchSteps = [
    {
      name: "Vector Search",
      description: "Semantic similarity matching",
      icon: "⚡",
      duration: 500
    },
    {
      name: "Scoring",
      description: "Calculating match scores",
      icon: "📊", 
      duration: 300
    }
  ];

  const steps = searchMode === 'ai-native' ? aiSearchSteps : fastSearchSteps;

  // Rotate tips every 5 seconds for AI-Native search
  useEffect(() => {
    if (!isSearching || searchMode !== 'ai-native') {
      setCurrentTipIndex(0);
      return;
    }

    const tipInterval = setInterval(() => {
      setCurrentTipIndex(prev => (prev + 1) % searchTips.length);
    }, 5000);

    return () => clearInterval(tipInterval);
  }, [isSearching, searchMode, searchTips.length]);

  useEffect(() => {
    if (!isSearching) {
      setCurrentStep(0);
      setStepProgress(0);
      setCurrentTipIndex(0);
      return;
    }

    let stepIndex = 0;
    let progress = 0;
    
    const interval = setInterval(() => {
      if (stepIndex < steps.length) {
        const step = steps[stepIndex];
        progress += (100 / step.duration) * 100; // Increment based on step duration
        
        if (progress >= 100) {
          progress = 0;
          stepIndex++;
          setCurrentStep(stepIndex);
        }
        
        setStepProgress(Math.min(100, progress));
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isSearching, searchMode, steps]);

  if (!isSearching) return null;

  const overallProgress = ((currentStep + (stepProgress / 100)) / steps.length) * 100;
  const currentStepInfo = steps[Math.min(currentStep, steps.length - 1)];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-elevated rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mb-4">
            {searchMode === 'ai-native' ? (
              <div className="w-20 h-20 mx-auto rounded-full overflow-hidden border-2 border-blue-500/25 shadow-lg">
                <img 
                  src="https://cdn.gamma.app/x7b9vzc41j0qqk1/generated-images/a1hd7w0iocFouW0VMX0Zm.png"
                  alt="AI Assistant Processing"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="text-4xl">⚡</div>
            )}
          </div>
          <h3 className="text-xl font-bold text-ink mb-2">
            {searchMode === 'ai-native' ? 'AI-Native Search' : 'Fast Search'} in Progress
          </h3>
          <p className="text-ink-muted">
            {searchMode === 'ai-native' 
              ? 'AI agents are analyzing your requirements...' 
              : 'Processing your search request...'
            }
          </p>
        </div>

        {/* Overall Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-ink-muted mb-2">
            <span>Overall Progress</span>
            <span>{Math.round(overallProgress)}%</span>
          </div>
          <div className="w-full bg-subtle rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                searchMode === 'ai-native' ? 'bg-blue-500/100' : 'bg-blue-500'
              }`}
              style={{ width: `${overallProgress}%` }}
            ></div>
          </div>
        </div>

        {/* Current Step */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-3">
            <span className="text-2xl">{currentStepInfo.icon}</span>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wide text-ink-subtle mb-0.5">
                Step {Math.min(currentStep + 1, steps.length)} of {steps.length} · In progress
              </div>
              <div className="font-semibold text-ink">{currentStepInfo.name}</div>
              <div className="text-sm text-ink-muted">{currentStepInfo.description}</div>
              {currentStep + 1 < steps.length && (
                <div className="mt-1.5 text-xs text-ink-subtle flex items-center gap-1">
                  <span>Up next:</span>
                  <span className="text-ink-muted font-medium">{steps[currentStep + 1].icon} {steps[currentStep + 1].name}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Step Progress */}
          <div className="w-full bg-subtle rounded-full h-1">
            <div 
              className={`h-1 rounded-full transition-all duration-200 ${
                searchMode === 'ai-native' ? 'bg-indigo-400' : 'bg-blue-300'
              }`}
              style={{ width: `${stepProgress}%` }}
            ></div>
          </div>
        </div>

        {/* Steps List */}
        <div className="mb-4 max-h-32 overflow-y-auto">
          <div className="space-y-2">
            {steps.map((step, index) => (
              <div key={index} className={`flex items-center space-x-3 p-2 rounded-lg transition-colors ${
                index < currentStep ? 'bg-green-500/10' :
                index === currentStep ? (searchMode === 'ai-native' ? 'bg-blue-500/10' : 'bg-accent-soft') :
                'bg-muted'
              }`}>
                <span className="text-lg">{step.icon}</span>
                <span className={`text-sm font-medium ${
                  index < currentStep ? 'text-green-400' :
                  index === currentStep ? (searchMode === 'ai-native' ? 'text-blue-400' : 'text-blue-400') :
                  'text-ink-subtle'
                }`}>
                  {step.name}
                </span>
                {index < currentStep && (
                  <span className="text-green-500 ml-auto">✓</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tips Section - Only for AI-Native Search */}
        {searchMode === 'ai-native' && (
          <div className="mb-4 p-3 bg-accent-soft rounded-lg border border-blue-500/25">
            <div className="flex items-start space-x-2">
              <span className="text-xl">{searchTips[currentTipIndex]?.icon}</span>
              <div className="flex-1">
                <p className="text-sm text-blue-300 font-medium transition-opacity duration-300">
                  {searchTips[currentTipIndex]?.text}
                </p>
              </div>
            </div>
            {/* Tip progress dots */}
            <div className="flex justify-center mt-2 space-x-1">
              {searchTips.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    index === currentTipIndex ? 'bg-blue-500 w-3' : 'bg-blue-500/30'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Cancel Button */}
        <button
          onClick={onCancel}
          className="w-full px-4 py-2 text-sm font-medium text-ink-muted bg-subtle border border-line rounded-lg hover:bg-subtle transition-colors"
        >
          Cancel Search
        </button>
      </div>
    </div>
  );
};

export default AISearchProgress;